import { buffer as turfBuffer } from '@turf/turf';
import { BRIGHT_COLORS } from '~/constants/color';
import { calculateLength } from '~/helpers/unitHelpers';
import type { LineObject as MapLineObject } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { ComputedPath, LngLat } from '../SurfaceBase';
import type VertexObject from '../VertexObject';
import { shouldConvertToMiles } from './common';

export default class LineObject extends CanvasObject {
  vertices: VertexObject[] = [];
  line: MapLineObject;

  firstCoordinate: LngLat;
  computedPath: ComputedPath;
  length;

  constructor(id: string, surface: SurfaceBase, line: MapLineObject) {
    super(id, surface);
    this.line = line;
    this.length = this.getLength();

    if (line.properties.buffer) {
      const bufferedPolygon = turfBuffer(line.geometry, line.properties.buffer, {
        units: 'feet',
      })!.geometry as GeoJSON.Polygon;
      this.computedPath = surface.getPath(bufferedPolygon.coordinates);
      this.firstCoordinate = {
        lng: bufferedPolygon.coordinates[0][0][0],
        lat: bufferedPolygon.coordinates[0][0][1],
      };
    } else {
      this.computedPath = surface.getLinePath(line.geometry.coordinates);
      this.firstCoordinate = {
        lng: line.geometry.coordinates[0][0],
        lat: line.geometry.coordinates[0][1],
      };
    }
  }

  getLength = () => {
    const store = this.getStore();
    const { currentUnitSystem: unitSystem } = store;

    const geometry = this.line.geometry;

    let l = calculateLength({ unitSystem, geometry });

    if (shouldConvertToMiles({ store, line: l })) {
      l = calculateLength({ unitSystem, unitType: 'miles', geometry });
    }

    return l;
  };

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    const { properties } = this.line;
    const { x, y } = this.surface.toScreenCoords(this.firstCoordinate);
    const scale = 2 ** (this.surface.map.getZoom() - this.computedPath.z);
    const r = this.surface.mapBearing;

    const { width, height } = this.computedPath.bounds;
    const maxWidth = Math.max(width, height);

    // if the total lineWidth is greater than the width/height of the object
    // then don't render the object at all, will be too far zoomed out.
    if (properties.strokeWidth / scale > maxWidth) {
      return;
    }

    ctx.save();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = (5 * properties.strokeWidth) / scale;
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.rotate(r);
    ctx.stroke(this.computedPath.path);
    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { properties } = this.line;
    const r = this.surface.mapBearing;
    const { x, y } = this.surface.toScreenCoords(this.firstCoordinate);
    const scale = 2 ** (this.surface.map.getZoom() - this.computedPath.z);

    const { width, height } = this.computedPath.bounds;
    const maxWidth = Math.max(width, height);

    // if the total lineWidth is greater than the width/height of the object
    // then don't render the object at all, will be too far zoomed out.
    if (properties.strokeWidth / scale > maxWidth) {
      return;
    }

    ctx.save();
    ctx.globalAlpha = properties.strokeOpacity;
    ctx.strokeStyle = properties.color;
    ctx.lineWidth = properties.strokeWidth / scale;
    properties.strokeStyle === 'dashed' && ctx.setLineDash([10, 4]);

    ctx.translate(x, y);
    ctx.scale(scale, scale);
    ctx.rotate(r);

    ctx.beginPath();
    ctx.stroke(this.computedPath.path);
    ctx.restore();

    const l = this.length;
    const padding = 10 / scale;
    const fontSize = 14 / scale;
    const labelOffset = 2 * padding;
    const lp = this.computedPath.labelData!.labelPoint;
    const textLines: string[] = [];

    ctx.save();
    ctx.font = `bold ${fontSize}px arial`;
    ctx.fillStyle = properties.color;
    ctx.translate(x, y);
    ctx.scale(scale, scale);

    ctx.rotate(r);
    ctx.translate(lp.x, lp.y);

    if (properties.showTitle && this.line.title) {
      textLines.push(this.line.title);
    }

    if (properties.showLength) {
      textLines.push(`${l.value.toLocaleString()} ${l.unit}`);
    }

    if (textLines.length === 0) {
      ctx.restore();
      return;
    }

    const textHeight = fontSize * textLines.length;
    let textWidth: number = 0;

    for (let i = 0; i < textLines.length; i++) {
      const t = textLines[i];
      const w = ctx.measureText(t).width;
      textWidth = Math.max(textWidth, w);
    }

    const lw = this.computedPath.bounds.width;
    let lr = this.computedPath.labelData!.rotation;

    if (!properties.labelAlwaysVisible && textWidth >= lw) {
      ctx.restore();
      return;
    }

    // only compute rotation updates if the map bearing is not zero
    if (r !== 0) {
      // computed label rotation plus the map's bearing.
      const ld = r + lr;

      if ((ld >= Math.PI / 2 && ld <= Math.PI) || ld <= -1 * Math.PI) {
        lr = lr - Math.PI;
      } else if (ld >= -1 * Math.PI && ld <= (-1 * Math.PI) / 2) {
        lr = lr - Math.PI;
      }
    }

    ctx.rotate(lr);
    ctx.beginPath();
    ctx.roundRect(
      -textWidth / 2 - padding / 2,
      -textHeight - padding - labelOffset,
      textWidth + padding,
      textHeight + padding + (textLines.length - 1) * padding,
      [5 / scale],
    );
    ctx.fill();

    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';
    ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
    const tx = 0;
    const ty = -textHeight - padding / 2 - labelOffset;

    for (let i = 0; i < textLines.length; i++) {
      ctx.fillText(textLines[i], tx, ty + i * fontSize + (i !== 0 ? padding : 0));
    }

    ctx.restore();
  }
}
